<template>
  <div class="relative -mt-0.5 flex min-w-0 flex-row gap-2 align-top">
    <div class="absolute bottom-2 left-[0.65rem] top-2 z-0 w-1 rounded-md bg-gray-100" />
    <div
      class="relative inline-block h-6 w-6 shrink-0 rounded-md p-1"
      :class="colors[changedField.type]"
    >
      <component :is="icons[changedField.type]" class="h-4 w-4" />
    </div>
    <div class="my-0.5 min-w-0 flex-1">
      <span class="font-semibold">
        {{ changedField.identifier }}
      </span>

      <div class="min-w-0">
        <ChangedFieldsList
          v-if="changedField.type === 'related'"
          :changed-fields="changedField.changedFields"
        />
        <ChangedFieldsList v-else-if="snapshotFields" :changed-fields="snapshotFields" />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import type {
  ChangeDeleted,
  ChangedCreated,
  ChangedFields,
  ChangedRelated,
} from '@carvia/ros-client-types';
import { PencilIcon, PlusCircleIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { computed } from 'vue';
import ChangedFieldsList from '@/components/audit-log/ChangedFieldsList.vue';

const props = defineProps<{
  changedField: ChangedRelated | ChangedCreated | ChangeDeleted;
}>();

function formatSnapshotValue(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

const snapshotFields = computed((): ChangedFields | null => {
  if (props.changedField.type !== 'created' && props.changedField.type !== 'deleted') {
    return null;
  }
  const value = props.changedField.value;
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, newValue]) => [
      key,
      { type: 'field' as const, oldValue: null, newValue: formatSnapshotValue(newValue) },
    ]),
  );
});

const icons = {
  created: PlusCircleIcon,
  related: PencilIcon,
  deleted: TrashIcon,
};

const colors = {
  created: 'text-green-500 bg-green-100',
  related: 'text-blue-500 bg-blue-100',
  deleted: 'text-red-500 bg-red-100',
};
</script>
